import {useEffect, useMemo, useState} from 'react';

import appStore from 'client/AppStore';
import {usePathname} from 'lib/router/usePathname';
import {callAction, useFluxStore} from 'client/framework';
import type {DropdownNavItem, MainNavItem, NavItems, NavItemZone} from './types';
import {getTeacherHasClassSubjects} from './GlobalNav.queries';
import {hasLicenseAccess as getHasLicenseAccess} from 'lib/UserAccessUtil';
import {Routes as StudentHomepageRoutes} from '../StudentHomepage/routes';
import sessionLifecycleActions from 'client/Session/SessionLifecycleActions';
import sessionStore from 'client/Session/SessionStore';
import {useHasTeacherDashboard} from 'client/components/FeatureFlag/useHasTeacherDashboard';
import {useStudentHomepageStore} from '../StudentHomepage/useStudentHomepageStore';

export default function useNavItems() {
  const homeRoute = useStudentHomepageStore(
    (state) => state.userDefaultRoute ?? StudentHomepageRoutes.Base
  );

  // Subscribed (not read off window.location): under the React Compiler this hook's
  // consumers only re-render when an input they subscribe to changes, so the pathname
  // must be one of those inputs or the active-link state freezes on the first route.
  const pathname = usePathname();

  const isSignUpPage = pathname.includes('/signup');
  const linkZone = useFluxStore(appStore, (s) => (s.isZoneDennis() ? 'dennis' : 'albert'));

  // Permissions information
  const hasAuthorAccess = useFluxStore(sessionStore, (s) => s.hasAuthorAccess());
  const hasContentManagerAccess = useFluxStore(sessionStore, (s) => s.hasContentManagerAccess());
  const hasTeacherAccess = useFluxStore(sessionStore, (s) => s.hasTeacherAccess());
  const hasValidSession = useFluxStore(sessionStore, (s) => s.hasValidSession());
  const isAuthor = useFluxStore(sessionStore, (s) => s.isAuthor());
  const isSchoolAdministrator = useFluxStore(sessionStore, (s) => s.isSchoolAdministrator());
  const isStudent = useFluxStore(sessionStore, (s) => s.isStudent());
  const isSuper = useFluxStore(sessionStore, (s) => s.isSuper());
  const isTeacher = useFluxStore(sessionStore, (s) => s.isTeacher());

  const [isTeacherWithClassSubjects, setIsTeacherWithClassSubjects] = useState(false);

  // Bridged: getHasLicenseAccess derives from the mandark cache, which re-evaluates
  // through the state tree's change event — a raw render read would be memoized once
  // by the compiler and never see the license queries settle.
  const isHasLicenseAccessReady = useFluxStore(sessionStore, () => getHasLicenseAccess().isReady);
  const hasLicenseAccess = useFluxStore(sessionStore, () => Boolean(getHasLicenseAccess().value));

  const {hasTeacherDashboard: hasTeacherNavV2} = useHasTeacherDashboard({
    skip: !hasValidSession
  });

  const NAV_ITEMS = useMemo<NavItems>(() => {
    const logOut: DropdownNavItem = {
      // callAction's payload/requestId params are read as `undefined` when omitted at
      // runtime, but framework.tsx types all three params as required. Passing explicit
      // `undefined` preserves the original single-arg semantics while satisfying the type.
      action: () => callAction(sessionLifecycleActions.END_SESSION),
      icon: ['fal', 'sign-out'],
      key: 'logout',
      name: 'Log Out'
    };

    return {
      albert: {
        dropdownNavItems: [
          {
            icon: ['fal', 'user'],
            key: 'account',
            name: 'My Account',
            showSeparator: true,
            route: '/account/profile'
          },
          {
            icon: ['fal', 'chart-line'],
            key: 'my-data',
            name: 'My Data',
            showSeparator: true,
            route: '/my-data'
          },
          {
            hasPermission: isAuthor && !hasContentManagerAccess,
            icon: ['fal', 'pen-nib'],
            key: 'authoring',
            name: 'Authoring',
            route: '/dennis/content/dashboard'
          },
          {
            hasPermission: hasContentManagerAccess,
            icon: ['fal', 'wrench'],
            key: 'dennis',
            name: 'Dennis',
            route: '/dennis'
          },
          {
            hardLink: true,
            hasPermission: !isStudent,
            icon: ['fal', 'book'],
            key: 'blog',
            name: 'Blog',
            route: 'https://www.albert.io/blog'
          },
          {
            hardLink: true,
            icon: ['fal', 'question-circle'],
            key: 'help',
            name: 'Help',
            route: 'https://help.albert.io/en/'
          },
          {
            hasPermission: isSuper || isTeacher,
            icon: ['fal', 'comment-pen'],
            key: 'feedback',
            name: 'Rate Albert'
          },
          logOut
        ],
        // Flag-on teachers get the v2 nav; flag-off keeps the untouched legacy list.
        mainNavItems: hasTeacherNavV2
          ? [
              {
                hasPermission: true,
                icon: ['far', 'home'],
                key: 'home',
                name: 'Home',
                route: homeRoute,
                // Every teacher-home surface keeps this entry active; course-library surfaces
                // (/subjects, /adaptive-subjects) stay owned by Library, matching legacy.
                routesArr: [
                  '/home',
                  '/assignments',
                  '/saved',
                  '/folders',
                  '/classes',
                  '/class-subjects',
                  '/skill-paths'
                ]
              },
              {
                hasPermission: !isTeacherWithClassSubjects,
                icon: ['far', 'books'],
                key: 'library',
                matchingRoutes: ['/subjects'],
                name: 'Library',
                route: '/subjects'
              },
              {
                hasPermission: isTeacherWithClassSubjects,
                icon: ['far', 'books'],
                key: 'library-class-subjects',
                matchingRoutes: ['/subjects'],
                name: 'Library',
                route: '/subjects/class-subjects'
              },
              {
                hasPermission: hasTeacherAccess,
                icon: ['far', 'chart-line'],
                key: 'reports',
                name: 'Reports',
                route: '/reports'
              },
              {
                hasPermission: isSchoolAdministrator || isSuper,
                key: 'admin',
                name: 'Admin',
                route: '/school-admin'
              },
              {
                hardLink: true,
                hasPermission: (isHasLicenseAccessReady && !hasLicenseAccess) || isSuper,
                key: 'pricing',
                name: 'Pricing',
                route: '/pricing-schools'
              }
            ]
          : [
              {
                hasPermission:
                  isStudent ||
                  (isAuthor && !hasTeacherAccess) ||
                  (isTeacher && !isTeacherWithClassSubjects),
                key: 'library',
                matchingRoutes: ['/subjects'],
                name: 'Library',
                route: '/subjects'
              },
              {
                hasPermission: isTeacher && isTeacherWithClassSubjects,
                key: 'library-class-subjects',
                matchingRoutes: ['/subjects'],
                name: 'Library',
                route: '/subjects/class-subjects'
              },
              {
                hasPermission: !isStudent,
                key: 'classes',
                name: 'Classes',
                route: '/classes'
              },
              {
                hasPermission: isStudent,
                icon: ['far', 'home'],
                key: 'home',
                name: 'Home',
                route: homeRoute,
                routesArr: ['/home']
              },
              {
                hasPermission: hasTeacherAccess,
                key: 'reports',
                name: 'Reports',
                route: '/reports'
              },
              {
                hasPermission: hasTeacherAccess,
                key: 'assignments',
                name: 'Assignments',
                route: '/assignments',
                routesArr: ['/saved', '/assignments', '/assignment-builder/create-assignment']
              },
              {
                hasPermission: isSchoolAdministrator || isSuper,
                key: 'admin',
                name: 'Admin',
                route: '/school-admin'
              },
              {
                hardLink: true,
                hasPermission: (isHasLicenseAccessReady && !hasLicenseAccess) || isSuper,
                key: 'pricing',
                name: 'Pricing',
                route: '/pricing-schools'
              }
            ]
      },
      dennis: {
        dropdownNavItems: [
          {
            hardLink: true,
            key: 'backToAlbert',
            name: 'Back to Albert',
            route: '/subjects'
          },
          logOut
        ],
        mainNavItems: [
          {
            hasPermission: hasAuthorAccess,
            key: 'content',
            name: 'Content',
            route: '/dennis/content'
          },
          {
            hasPermission: isSuper,
            key: 'users',
            name: 'Users',
            route: '/dennis/users'
          },
          {
            hasPermission: isSuper,
            key: 'districts',
            name: 'Districts',
            route: '/dennis/districts'
          },
          {
            hasPermission: isSuper,
            key: 'schools',
            name: 'Schools',
            route: '/dennis/schools'
          }
        ]
      }
    };
  }, [
    hasAuthorAccess,
    hasContentManagerAccess,
    hasLicenseAccess,
    hasTeacherAccess,
    hasTeacherNavV2,
    homeRoute,
    isAuthor,
    isHasLicenseAccessReady,
    isSchoolAdministrator,
    isStudent,
    isSuper,
    isTeacher,
    isTeacherWithClassSubjects
  ]);

  useEffect(() => {
    let cancelled = false;
    if (isTeacher) {
      (async () => {
        const teacherHasClassSubjects = await getTeacherHasClassSubjects();
        if (!cancelled) {
          setIsTeacherWithClassSubjects(teacherHasClassSubjects);
        }
      })();
    }
    return () => {
      cancelled = true;
    };
  }, [isTeacher, setIsTeacherWithClassSubjects]);

  const filteredNavItems: NavItemZone = useMemo(() => {
    if (!hasValidSession && isSignUpPage) {
      return {
        dropdownNavItems: [],
        mainNavItems: []
      };
    }

    const dropdownNavItems = NAV_ITEMS[linkZone].dropdownNavItems.filter(
      ({hasPermission = true}) => hasPermission
    );
    const mainNavItems = NAV_ITEMS[linkZone].mainNavItems.filter(
      ({hasPermission = true}) => hasPermission
    );

    return {
      dropdownNavItems,
      mainNavItems
    };
  }, [hasValidSession, isSignUpPage, linkZone, NAV_ITEMS]);

  function getIsMatchingRoute(navItem: MainNavItem) {
    const {matchingRoutes = [], route: linkRoute, routesArr = []} = navItem;

    let ret = false;

    if (routesArr.length > 0) {
      ret = routesArr.some((route) => pathname.startsWith(route));
    } else if (
      /**
       * This ensures that the Subjects header link will contain the active class
       * regardless of which group/subject combination is rendered. It will be replaced soon.
       */
      matchingRoutes.includes(pathname) ||
      linkRoute === pathname ||
      pathname.startsWith(linkRoute) ||
      matchingRoutes.filter((path) => pathname.indexOf(path) === 0).length > 0
    ) {
      ret = true;
    }
    return ret;
  }

  return {
    dropdownNavItems: filteredNavItems.dropdownNavItems,
    getIsMatchingRoute,
    mainNavItems: filteredNavItems.mainNavItems
  };
}
